/*
 * Copyright 2026 North Carolina State University
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Checkbox,
  CircularProgress,
  Chip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider
} from '@mui/material';
import useFacility from '../../hooks/useFacility';
import { calculateDays } from '../../utils/invoiceCalculations';

// Helper function to format date as YYYY-MM-DD in LOCAL timezone (not UTC)
// This prevents the "off by one day" issue when using toISOString()
const formatLocalDate = (date) => {
  if (!date) return '';
  const d = date instanceof Date ? date : new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Helper function to parse a date from database as a local date (avoiding timezone shift)
// When a date is stored as "2025-01-01T00:00:00.000Z", this ensures we get Jan 1 locally
const parseAsLocalDate = (dateValue) => {
  if (!dateValue) return null;
  if (dateValue instanceof Date) {
    // If already a Date, extract UTC parts and create local date
    return new Date(dateValue.getUTCFullYear(), dateValue.getUTCMonth(), dateValue.getUTCDate(), 12, 0, 0, 0);
  }
  // If string, parse and create local date from UTC parts
  const d = new Date(dateValue);
  return new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), 12, 0, 0, 0);
};

const BatchInvoicing = ({ open, onClose }) => {
  // Get admin's facility preference
  const { facilityPreference, loading: prefLoading } = useFacility();
  
  // Initialize projects as an empty array
  const [projects, setProjects] = useState([]);
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [projectsData, setProjectsData] = useState({});
  
  // Filter states
  const [facilities, setFacilities] = useState([]);
  const [selectedFacility, setSelectedFacility] = useState('all');
  // Accounts Receivable target applied to every invoice in the batch. This is the
  // "Project-Phase to receive the revenue" on the business-office billing request.
  const [arFacility, setArFacility] = useState('');
  const [arAccount, setArAccount] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Set facility filter from admin preference when loaded
  useEffect(() => {
    if (!prefLoading && facilityPreference) {
      setSelectedFacility(facilityPreference);
    }
  }, [facilityPreference, prefLoading]);
  
  // Available statuses for batch invoicing
  const eligibleStatuses = ['Approved', 'In Progress', 'Completed', 'Revision Required'];

  // Fetch facilities
  const fetchFacilities = async () => {
    try {
      const response = await axios.get('/api/facilities');
      setFacilities(response.data);
    } catch (error) {
      console.error('Error fetching facilities:', error);
      toast.error('Failed to fetch facilities');
    }
  };

  // Check if project is eligible for invoicing (basic check - unbilled periods checked separately)
  const isProjectEligible = (project) => {
    // Include projects with eligible statuses
    if (!eligibleStatuses.includes(project.project_status)) {
      return false;
    }
    
    // Check if project has active chamber assignments (new schema uses assignments array)
    const hasActiveChambers = project.chamber_assignments && 
      project.chamber_assignments.some(chamber => 
        (chamber.assignments && chamber.assignments.some(a => a.status === 'Active')) ||
        (chamber.units && chamber.units.some(unit => unit.status === 'Active')) // fallback for old schema
      );
    
    // Check if project has active resource assignments  
    const hasActiveResources = project.resource_assignments && 
      project.resource_assignments.length > 0;
    
    // Project must have either active chambers or resources
    return hasActiveChambers || hasActiveResources;
  };

  // Fetch all active projects
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        setLoading(true);
        const response = await axios.get('/api/admin/projects/detailed');
        
        // Ensure we always set an array, even if the response is invalid
        const projectsData = Array.isArray(response.data) ? response.data : [];
        
        // Filter projects to only include eligible ones
        const eligibleProjects = projectsData.filter(isProjectEligible);
        
        // Process projects to get their unbilled periods (same logic as regular invoicing)
        const processedProjects = await Promise.all(eligibleProjects.map(async (project) => {
          try {
            // Fetch unbilled periods using the same endpoint as regular invoicing
            const unbilledResponse = await axios.get(`/api/invoices/unbilled-periods/${project._id}`);
            const unbilledData = unbilledResponse.data || {};
            // API returns unbilled_periods (underscore) with nested unbilled_periods array
            const chamberUnbilledPeriods = unbilledData.unbilled_periods || [];
            
            
            // Calculate total unbilled days across all chamber assignments
            let totalUnbilledDays = 0;
            let earliestUnbilledStart = null;
            let latestUnbilledEnd = null;
            
            // Today's calendar date for capping unbilled periods (local noon,
            // not 23:59 — that timestamp becomes the next UTC day).
            const today = new Date();
            today.setHours(12, 0, 0, 0);
            
            chamberUnbilledPeriods.forEach(chamberUnit => {
              // Each chamberUnit has an unbilled_periods array (underscore)
              (chamberUnit.unbilled_periods || []).forEach(period => {
                const startDate = parseAsLocalDate(period.start_date);
                let endDate = parseAsLocalDate(period.end_date);
                if (!startDate || !endDate) return;
                if (endDate > today) endDate = today;
                if (startDate > today) return;
                
                const days = calculateDays(startDate, endDate);
                totalUnbilledDays += days; // Just calendar days
                
                // Track earliest start and latest end for billing period
                if (!earliestUnbilledStart || startDate < earliestUnbilledStart) {
                  earliestUnbilledStart = startDate;
                }
                if (!latestUnbilledEnd || endDate > latestUnbilledEnd) {
                  latestUnbilledEnd = endDate;
                }
              });
            });
            
            
            // Get PI information - handle populated role objects
            const pi = project.users?.find(u => {
              if (!u.role) return false;
              if (typeof u.role === 'object' && u.role !== null) {
                const roleName = u.role.role || u.role.name || '';
                return roleName === 'PI' || roleName === 'Principal Investigator';
              }
              return u.role === 'PI' || u.role === 'Principal Investigator';
            });
            const displayUser = pi || project.users?.[0];
            const piName = displayUser ? `${displayUser.first_name} ${displayUser.last_name}` : 'N/A';

            // Only include projects with at least 1 unbilled day
            if (totalUnbilledDays < 1) {
              return null;
            }

            return {
              ...project,
              pi_name: piName,
              pi_user: displayUser,
              unbilledPeriodsData: unbilledData,
              chamberUnbilledPeriods, // Store for invoice generation
              totalUnbilledDays,
              earliestUnbilledStart,
              latestUnbilledEnd,
              hasUnbilledPeriod: totalUnbilledDays > 0
            };
          } catch (error) {
            console.error(`Error fetching unbilled periods for project ${project._id}:`, error);
            // If error occurs, still include project but with unknown unbilled status
            const pi = project.users?.find(u => {
              if (!u.role) return false;
              if (typeof u.role === 'object' && u.role !== null) {
                const roleName = u.role.role || u.role.name || '';
                return roleName === 'PI' || roleName === 'Principal Investigator';
              }
              return u.role === 'PI' || u.role === 'Principal Investigator';
            });
            const displayUser = pi || project.users?.[0];
            const piName = displayUser ? `${displayUser.first_name} ${displayUser.last_name}` : 'N/A';
            
            // On error, include project anyway - it might have unbilled periods
            // Better to show and let the invoice generation handle it
            return {
              ...project,
              pi_name: piName,
              pi_user: displayUser,
              unbilledPeriodsData: null,
              chamberUnbilledPeriods: [],
              totalUnbilledDays: -1, // -1 indicates unknown
              earliestUnbilledStart: null,
              latestUnbilledEnd: null,
              hasUnbilledPeriod: true // Include anyway on error
            };
          }
        }));
        
        // Filter out projects with no unbilled periods (keep those with errors or unbilled days)
        const validProjects = processedProjects.filter(project => 
          project !== null && (project.hasUnbilledPeriod || project.totalUnbilledDays === -1)
        );
        
        setProjects(validProjects);
      } catch (error) {
        console.error('Error fetching projects:', error);
        toast.error('Failed to fetch projects: ' + error.message);
        setProjects([]);
      } finally {
        setLoading(false);
      }
    };

    if (open) {
      fetchFacilities();
      fetchProjects();
    } else {
      // Reset state when dialog closes
      setProjects([]);
      setSelectedProjects([]);
      setProjectsData({});
      setSelectedFacility('all');
      setSelectedStatus('all');
      setSearchTerm('');
    }
  }, [open]);

  const handleSelectProject = (projectId) => {
    setSelectedProjects(prev => {
      if (prev.includes(projectId)) {
        return prev.filter(id => id !== projectId);
      } else {
        return [...prev, projectId];
      }
    });
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedProjects(filteredProjects.map(project => project._id));
    } else {
      setSelectedProjects([]);
    }
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    // Use UTC parts so calendar dates (billing periods) display exactly as stored,
    // without a timezone shift.
    const d = new Date(date);
    return `${d.getUTCMonth() + 1}/${d.getUTCDate()}/${d.getUTCFullYear()}`;
  };

  // Filter projects based on search term, facility, and status
  const filteredProjects = projects.filter(project => {
    const matchesSearch = searchTerm === '' || 
      project.project_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      project.project_title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      project.pi_name?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesFacility = selectedFacility === 'all' || 
      project.facility?._id === selectedFacility;
    
    const matchesStatus = selectedStatus === 'all' || 
      project.project_status === selectedStatus;
    
    return matchesSearch && matchesFacility && matchesStatus;
  });

  const handleGenerateInvoices = async () => {
    if (!arFacility || !arAccount) {
      toast.error('Select the Accounts Receivable facility and account that will receive this revenue.');
      return;
    }
    try {
      setProcessing(true);
      const successfulInvoices = [];
      const failedInvoices = [];

      for (const projectId of selectedProjects) {
        const project = projects.find(p => p._id === projectId);
        if (!project) continue;

        try {

          // Use the unbilled periods data to determine billing period
          // The bill period should cover all unbilled periods up to today
          const today = new Date();
          today.setHours(12, 0, 0, 0);
          
          // Bill start date is the earliest unbilled period start
          let billStartDate = project.earliestUnbilledStart 
            ? parseAsLocalDate(project.earliestUnbilledStart)
            : parseAsLocalDate(project.project_start_date || project.start_date);
          
          // Bill end date is the latest unbilled period end, but capped at today
          let billEndDate = project.latestUnbilledEnd
            ? parseAsLocalDate(project.latestUnbilledEnd)
            : today;
          
          // Cap end date to today if it's in the future
          if (billEndDate > today) {
            billEndDate = today;
          }

          // Validate bill dates
          if (!billStartDate || isNaN(billStartDate.getTime())) {
            continue;
          }

          // Skip if bill start date is after bill end date
          if (billStartDate > billEndDate) {
            continue;
          }

          // Validate project has necessary data
          if (!project.project_id) {
            throw new Error('Proposal ID is missing');
          }

          // Use backend API for calculations (unified billing system)
          // This is the same approach used in regular invoice generation
          let invoiceDetails;
          try {
            const calcResponse = await axios.post('/api/invoices/calculate', {
              project_id: project._id,
              bill_start_date: formatLocalDate(billStartDate),
              bill_end_date: formatLocalDate(billEndDate)
            });
            invoiceDetails = calcResponse.data;
          } catch (calcError) {
            console.error(`Failed to calculate invoice for ${project.project_id}:`, calcError);
            failedInvoices.push(project.project_id);
            continue;
          }

          // Extract costs from backend response
          const chamberCosts = {
            unitCosts: invoiceDetails.chamberUsage || [],
            totalCost: invoiceDetails.chamberTotal || 0
          };
          const resourceCosts = {
            resources: invoiceDetails.resourceUsage || [],
            totalCost: invoiceDetails.resourceTotal || 0
          };
          const totalAmount = invoiceDetails.totalAmount || 0;

          if (totalAmount <= 0) {
            continue;
          }

          // Get the PI for 100% distribution - use stored pi_user or find again
          const pi = project.pi_user || project.users?.find(u => {
            if (!u.role) return false;
            if (typeof u.role === 'object' && u.role !== null) {
              const roleName = u.role.role || u.role.name || '';
              return roleName === 'PI' || roleName === 'Principal Investigator';
            }
            return u.role === 'PI' || u.role === 'Principal Investigator';
          }) || project.users?.[0]; // Fallback to first user
          
          if (!pi) {
            throw new Error('No users found for project');
          }

          // Format dates properly for backend (yyyy-MM-dd format)
          // Uses formatLocalDate to avoid timezone shift issues
          const formatDateForBackend = (date) => {
            return formatLocalDate(date);
          };

          // Prepare invoice data. Distribution is no longer set by admins - the PI
          // and designated payers choose accounts once the invoice is sent to users.
          // The backend API returns chamberUsage with fields: chamber_id, unit_id, chamber_name, unit_name, quantity, rate, start_date, end_date, days, total_cost
          const invoiceData = {
            project_id: project.project_id,
            bill_start_date: formatDateForBackend(billStartDate),
            bill_end_date: formatDateForBackend(billEndDate),
            chamber_usage: chamberCosts.unitCosts.map(unit => ({
              chamber_id: unit.chamber_id,
              assignment_id: unit._assignment_id,
              unit_id: unit.unit_id,
              start_date: formatDateForBackend(unit.start_date),
              end_date: formatDateForBackend(unit.end_date),
              quantity: unit.quantity,
              total_days: unit.days,
              rate: unit.rate,
              rate_basis: unit.rate_basis,
              total_cost: unit.total_cost
            })),
            resource_usage: resourceCosts.resources.map(res => ({
              resource_id: res.resource_id,
              assignment_id: res.assignment_id,
              quantity: res.unbilled_quantity,
              rate: res.rate,
              total_cost: res.total_cost
            })),
            total_amount: totalAmount,
            account_mapping: {
              facility_id: arFacility,
              account_number: arAccount
            }
          };

          // Create invoice
          const response = await axios.post('/api/invoices', invoiceData);
          successfulInvoices.push(project.project_id);
        } catch (error) {
          console.error(`Failed to generate invoice for project ${project.project_id}:`, error);
          failedInvoices.push(project.project_id);
        }
      }

      // Show results
      if (successfulInvoices.length > 0) {
        toast.success(`Successfully generated ${successfulInvoices.length} invoices`);
      }
      if (failedInvoices.length > 0) {
        toast.error(`Failed to generate invoices for ${failedInvoices.length} projects`);
      }

      // Close dialog and refresh data
      onClose();
    } catch (error) {
      console.error('Error in batch invoice generation:', error);
      toast.error('Failed to generate batch invoices');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          height: '90vh',
          maxHeight: '90vh'
        }
      }}
    >
      <DialogTitle>
        Batch Invoice Generation
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ mt: 2 }}>
            {/* Filters */}
            <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Search Projects"
                    placeholder="Search by Proposal ID, Title, or PI"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Facility</InputLabel>
                    <Select
                      value={selectedFacility}
                      onChange={(e) => setSelectedFacility(e.target.value)}
                      label="Facility"
                    >
                      <MenuItem value="all">All Facilities</MenuItem>
                      {facilities.map(facility => (
                        <MenuItem key={facility._id} value={facility._id}>
                          {facility.facility_name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Project Status</InputLabel>
                    <Select
                      value={selectedStatus}
                      onChange={(e) => setSelectedStatus(e.target.value)}
                      label="Project Status"
                    >
                      <MenuItem value="all">All Statuses</MenuItem>
                      {eligibleStatuses.map(status => (
                        <MenuItem key={status} value={status}>
                          {status}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Typography variant="body2" color="text.secondary">
                    Showing {filteredProjects.length} projects with unbilled periods
                  </Typography>
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                Accounts Receivable *
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                The facility account that will receive the revenue for every invoice in this batch.
                Sent to the business office as the &ldquo;Project-Phase to receive the revenue&rdquo;.
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth size="small" required error={!arFacility}>
                    <InputLabel>Revenue Facility</InputLabel>
                    <Select
                      value={arFacility}
                      onChange={(e) => { setArFacility(e.target.value); setArAccount(''); }}
                      label="Revenue Facility"
                    >
                      {facilities.map(facility => (
                        <MenuItem key={facility._id} value={facility._id}>
                          {facility.facility_name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={5}>
                  <FormControl
                    fullWidth
                    size="small"
                    required
                    disabled={!arFacility}
                    error={!!arFacility && !arAccount}
                  >
                    <InputLabel>Revenue Account</InputLabel>
                    <Select
                      value={arAccount}
                      onChange={(e) => setArAccount(e.target.value)}
                      label="Revenue Account"
                    >
                      {(facilities.find(f => f._id === arFacility)?.accounts_receivable || []).map((account, index) => (
                        <MenuItem key={index} value={account.account_number || ''}>
                          {account.account_number || 'N/A'} - {account.description || 'No description'}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Paper>

            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        indeterminate={selectedProjects.length > 0 && selectedProjects.length < filteredProjects.length}
                        checked={selectedProjects.length === filteredProjects.length && filteredProjects.length > 0}
                        onChange={handleSelectAll}
                      />
                    </TableCell>
                    <TableCell>Proposal ID</TableCell>
                    <TableCell>Title</TableCell>
                    <TableCell>PI</TableCell>
                    <TableCell>Facility</TableCell>
                    <TableCell>Unbilled Days</TableCell>
                    <TableCell>Project Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredProjects.map((project) => (
                    <TableRow
                      key={project._id}
                      hover
                      selected={selectedProjects.includes(project._id)}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedProjects.includes(project._id)}
                          onChange={() => handleSelectProject(project._id)}
                        />
                      </TableCell>
                      <TableCell>{project.project_id}</TableCell>
                      <TableCell>{project.project_title || project.title}</TableCell>
                      <TableCell>{project.pi_name}</TableCell>
                      <TableCell>{project.facility?.facility_name || 'N/A'}</TableCell>
                      <TableCell>
                        <Chip 
                          label={project.totalUnbilledDays === -1 ? 'Unknown' : `${project.totalUnbilledDays} days`}
                          color={project.totalUnbilledDays === -1 ? 'default' : 
                                 project.totalUnbilledDays > 30 ? 'warning' : 'info'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={project.project_status}
                          color={project.project_status === 'In Progress' ? 'success' : 
                                 project.project_status === 'Completed' ? 'default' : 'primary'}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button 
          variant="contained" 
          color="primary"
          disabled={selectedProjects.length === 0 || processing || !arFacility || !arAccount}
          onClick={handleGenerateInvoices}
        >
          {processing ? 'Processing...' : 'Generate Invoices'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BatchInvoicing; 